import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button } from '@mui/material';
import { AddBoxIcon, BankIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import TitleIcon from '@sharedCR/coreDesignSystem/TitleIcon';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import useSort from 'hooks/useSort';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { getNotFoundTableData } from '../../Content.utils';
import {
  defaultForm,
  defaultInitialEntitySideData,
  pagesPropTypes,
} from '../MainContent.constants';
import { ContentHeadline } from '../sharedComponents';

import { getCreditTableConfig } from './Credits.config';
import { CreditTableColumnId, defaultCreditSortOrder } from './Credits.constants';
import { containerStyles, tableWrapperStyles } from './Credits.styles';
import {
  addFormToTableData,
  createNewTableData,
  normalizeFormData,
  prepareModifiedFields,
} from './Credits.utils';

const defaultPendingChanges = {};

const Credits = ({
  isLoading,
  form = defaultForm,
  className,
  applicationData,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultInitialEntitySideData,
  setIsSavingAllowed,
  pendingChanges,
}) => {
  const [editableCreditData, setEditableCreditData] = useState(null);

  const { order, onRequestSort } = useSort(defaultCreditSortOrder);

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : form;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const creditsTableData = applicationData?.credits;
  const pendingChangesData = pendingChanges?.credits || defaultPendingChanges;

  const { credits } = entitySideData;
  const isEditActive = !!editableCreditData;
  const isCreditsExist = !!credits?.length;

  useEffect(() => {
    setIsSavingAllowed(!isEditActive);
  }, [isEditActive, setIsSavingAllowed]);

  useEffect(() => {
    return () => {
      setEntitySideData(prevState => {
        if (!prevState.credits) {
          return prevState;
        }

        return {
          ...prevState,
          credits: prevState.credits.filter(({ isNewlyAdded }) => !isNewlyAdded),
        };
      });
    };
  }, [setEntitySideData]);

  const creditsInitialValuesMap = useMemo(() => {
    return initialEntitySideData.credits ? _.keyBy(initialEntitySideData.credits, 'id') : {};
  }, [initialEntitySideData.credits]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitySideData(prevState => ({
          ...prevState,
          credits: prevState.credits.filter(({ id }) => id !== data.id),
        }));
      }

      setEditableCreditData(null);
    },
    [setEntitySideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = normalizeFormData({ form: updatedData, initialData });

      setEntitySideData(prevState => ({
        ...prevState,
        credits: prevState.credits.map(entity =>
          entity.id === dataToSave.id ? dataToSave : entity,
        ),
      }));
      setEditableCreditData(null);
    },
    [setEntitySideData],
  );

  const deleteCredit = useCallback(
    ({ id }) => {
      setEntitySideData(prevState => ({
        ...prevState,
        credits: prevState.credits.reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));
      setEditableCreditData(null);
    },
    [setEntitySideData],
  );

  const orderedCreditsWithPendingChangesAndForm = useMemo(() => {
    const orderedCredits = _.orderBy(
      credits,
      [CreditTableColumnId.IS_NEWLY_ADDED, order.id],
      ['asc', order.direction],
    );

    const orderedCreditsWithPendingChanges = prepareModifiedFields({
      currentData: orderedCredits,
      pendingChangesData,
      initialValuesMap: creditsInitialValuesMap,
    });
    const orderedCreditsWithPendingChangesAndForm = editableCreditData
      ? addFormToTableData({
          tableData: orderedCreditsWithPendingChanges,
          editData: editableCreditData,
          cancelForm,
          isLoading,
          pendingChanges: pendingChangesData,
          saveForm,
          creditsList: orderedCredits,
        })
      : orderedCreditsWithPendingChanges;

    return orderedCreditsWithPendingChangesAndForm;
  }, [
    credits,
    cancelForm,
    saveForm,
    isLoading,
    creditsInitialValuesMap,
    editableCreditData,
    order.direction,
    order.id,
    pendingChangesData,
  ]);

  const correctedCreditsTableData = isCreditsExist
    ? orderedCreditsWithPendingChangesAndForm
    : getNotFoundTableData('creditType');

  useEffect(() => {
    if (!creditsTableData) {
      return;
    }

    const isCreditsExist = _.has(entitySideData, 'credits');

    if (isCreditsExist) {
      return;
    }

    setEntitySideData(prevState => ({
      ...prevState,
      credits: creditsTableData,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      credits: creditsTableData,
    }));
  }, [creditsTableData, entitySideData, setEntitySideData, setInitialEntitySideData]);

  const tableConfig = useMemo(() => {
    return getCreditTableConfig({
      setEditableTableData: setEditableCreditData,
      deleteCredit,
      editableRowId: editableCreditData?.id,
      isActionDisabled: isLoading,
    });
  }, [deleteCredit, editableCreditData?.id, isLoading]);

  const addNewTableEntity = useCallback(() => {
    const entityData = createNewTableData();

    setEntitySideData(prevState => ({
      ...prevState,
      credits: !prevState.credits ? [entityData] : [...prevState.credits, entityData],
    }));
    setEditableCreditData(entityData);
  }, [setEntitySideData]);

  return (
    <Box className={className} sx={containerStyles}>
      <ContentHeadline
        controls={
          <Button
            data-testid={PropertyAndLoanTestIds.CREDITS_ADD_BUTTON}
            disabled={isEditActive || isLoading}
            size="small"
            startIcon={<AddBoxIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={addNewTableEntity}
          >
            Add Credit
          </Button>
        }
        iconNode={<TitleIcon icon={BankIcon} />}
        title="Credits"
      />
      <TableCDS
        isHideTableWhenEmpty
        config={tableConfig}
        customStyles={tableWrapperStyles}
        data={correctedCreditsTableData}
        isCustomDataDefinition={!isCreditsExist}
        isLoading={isDataLoading}
        isSortDisabled={isEditActive}
        order={order}
        pendingChangesTextSelector="> span"
        onRequestSort={onRequestSort}
      />
    </Box>
  );
};

Credits.propTypes = pagesPropTypes;

export default memo(Credits);
